import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
import {
  PutObjectCommand,
  DeleteObjectCommand,
  S3Client,
} from '@aws-sdk/client-s3';
import { fromCognitoIdentity } from '@aws-sdk/credential-provider-cognito-identity';
import { FormikValues, useFormikContext } from 'formik';
import React, {
  ChangeEvent,
  DragEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import Botao from 'Component/Botao';
import Icone from 'Component/Icone';
import { useGetCognitoTokenQuery } from 'View/Service/Default/Cognito';

import Ajuda from './Ajuda';
import { FormContexto } from './Form';

interface Props {
  ajuda?: string;
  name: string;
  multiple?: boolean;
  path?: string;
}

const DropZone = ({ name, multiple, ajuda, path = 'tmp' }: Props): JSX.Element => {
  const [s3, setS3] = useState<S3Client>();
  const [files, setFiles] = useState<
    {
      file?: File;
      uploaded: boolean;
      uploading: boolean;
      error: boolean;
      url?: string;
    }[]
  >([]);
  const [dragging, setDragging] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const { getFieldMeta, getFieldHelpers } = useFormikContext<FormikValues>();
  const { value, error, touched } = getFieldMeta(name);
  const { setError, setTouched, setValue } = getFieldHelpers(name);
  const { regras } = useContext(FormContexto);

  const { data: cognito, refetch } = useGetCognitoTokenQuery();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(refetch, []);

  useEffect(() => {
    if (value && typeof value == 'string') {
      setFiles([{
        uploaded: true,
        uploading: false,
        error: false,
        url: value,
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!value && files) {
      setFiles([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (cognito) {
      setS3(
        new S3Client({
          region: cognito.region,
          credentials: fromCognitoIdentity({
            client: new CognitoIdentityClient({ region: cognito.region }),
            identityId: cognito.id,
            logins: {
              'cognito-identity.amazonaws.com': cognito.token,
            },
          }),
        })
      );
    }
  }, [cognito]);

  useEffect(() => {
    // setValue(files, true);
  }, [files, name, setValue]);

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setDragging(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setDragging(false);
  };

  const handleDrop = async (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setDragging(false);

    if (!touched) {
      setTouched(true, false);
    }

    setError(undefined);

    try {
      await regras?.validateAt(name, {
        [name]: multiple ? e.dataTransfer?.files : e.dataTransfer?.files[0],
      });

      if (multiple) {
        const newFiles = [...(e.dataTransfer?.files ?? [])];
        const mapFiles = newFiles.map((file) => ({
          uploaded: false,
          uploading: false,
          error: false,
          file,
        }));

        setFiles([...files, ...mapFiles]);
      } else {
        setFiles([
          {
            uploaded: false,
            uploading: false,
            error: false,
            file: e.dataTransfer?.files[0] ?? null,
          },
        ]);
      }
    } catch (e) {
      setError((e as { errors: string[] }).errors[0]);
    }
  };

  const handleSelectFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!touched) {
      setTouched(true, false);
    }

    setError(undefined);

    try {
      const inputFiles = e.currentTarget.files;

      await regras?.validateAt(name, {
        [name]: multiple
          ? inputFiles
          : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            inputFiles![0],
      });

      if (multiple) {
        const newFiles = [...(inputFiles ?? [])];
        const mapFiles = newFiles.map((file) => ({
          uploaded: false,
          uploading: false,
          error: false,
          file,
        }));

        setFiles([...files, ...mapFiles]);
      } else {
        setFiles([
          {
            uploaded: false,
            uploading: false,
            error: false,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            file: inputFiles![0],
          },
        ]);
      }
    } catch (e) {
      setError((e as { errors: string[] }).errors[0]);
    }
  };

  const openFileDialog = () => {
    input && input.current && input.current.click();
  };

  const remove = (index: number) => {
    if (s3) {
      setTouched(true);

      const params = {
        Bucket: cognito?.bucket,
        Key: files[index].url?.replace(`${cognito?.url}/`, ''),
      };

      const newFiles = [...files];
      newFiles[index].uploading = true;
      setFiles([...newFiles]);

      try {
        s3.send(new DeleteObjectCommand(params)).then(() => {
          setFiles([...files.filter((file, i) => i != index)]);
        });
      } catch (e) {
        const newFiles = [...files];
        newFiles[index].uploaded = true;
        newFiles[index].uploading = false;
        setFiles([...newFiles]);
      }
    }
  };

  const upload = (index: number, file: File) => {
    if (s3) {
      const fileName = Math.floor(Math.random() * 10000000000);
      const extension = file.name.split('.').at(-1);
      const key = `${path}/${fileName}.${extension}`;
      const params = {
        Bucket: cognito?.bucket,
        Key: key,
        Body: file,
      };

      const newFiles = [...files];
      newFiles[index].uploading = true;
      setFiles([...newFiles]);

      try {
        s3.send(new PutObjectCommand(params)).then(() => {
          const newFiles = [...files];
          newFiles[index].uploaded = true;
          newFiles[index].uploading = false;
          newFiles[index].url = `${cognito?.url}/${key}`;
          setFiles([...newFiles]);
        });
      } catch (e) {
        const newFiles = [...files];
        newFiles[index].uploading = false;
        newFiles[index].error = true;
        setFiles([...newFiles]);
      }
    }
  };

  useEffect(() => {
    if (files.length) {
      files.forEach((file, index) => {
        if (!file.uploaded && !file.uploading && !file.error) {
          upload(index, file.file as File);
        }
      });

      setValue(
        multiple
          ? [
              ...files
                .filter((file) => file.uploaded && file.url)
                .map((file) => file.url),
            ]
          : files.length && files[0].uploaded && files[0].url
          ? files[0].url
          : '',
        false
      );
    } else {
      if (touched) {
        setValue(multiple ? [] : '', false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  return (
    <div className="mb-3">
      <div
        className={`d-block px-3 border border-3 rounded-2 border-dashed fs-5 fw-light text-center position-relative ${
          !files.length ? 'py-5' : ''
        } ${!error ? 'border-gray text-muted' : 'border-danger text-danger'}`}
      >
        <input
          ref={input}
          type="file"
          className="d-none"
          multiple={multiple}
          onChange={handleSelectFiles}
        />
        <>
          {files?.length ? (
            files.map((file, index) => (
              <div
                key={Math.random().toString()}
                className="d-flex justify-content-between align-items-center py-2 gap-3"
              >
                <div className="d-flex flex-column align-items-start flex-grow-1 text-truncate">
                  <div className="text-body text-truncate">
                    {file.uploading && (
                      <div
                        className="spinner-grow spinner-grow-sm text-primary me-2"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    )}
                    {file.file?.name || file.url?.split('/').pop()}
                  </div>
                  <small>{file.file?.type}</small>
                </div>
                <div className="d-flex gap-3 align-items-center text-nowrap">
                  <strong className="text-body fw-bold">
                    {file.file?.size.toHumanFileSize()}
                  </strong>
                  {file.uploaded && (
                    <Botao
                      icone="trash"
                      tema="danger"
                      onClick={() => remove(index)}
                      processando={file.uploading}
                    />
                  )}
                </div>
              </div>
            ))
          ) : (
            <>
              <Icone
                nome={dragging ? 'cloud-arrow-up' : 'card-image'}
                className="display-1 d-block"
              />
              {error && touched
                ? error
                : dragging
                ? 'Solta o arquivo para fazer o upload'
                : 'Arraste e solte um arquivo aqui ou clique para busca-lo em seu dispositivo'}
            </>
          )}
          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onClick={openFileDialog}
            className="position-absolute top-0 bottom-0 start-0 cursor-pointer"
            style={{ right: files.length ? 100 : 0 }}
          />
        </>
      </div>
      {ajuda && <Ajuda>{ajuda}</Ajuda>}
    </div>
  );
};

export default DropZone;
